import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/api';
import { createSanitizedMarkup } from '../utils/sanitize';

// In-memory translation cache (shared across all instances)
const translationCache = new Map();

// Generate cache key
const getCacheKey = (text, targetLang, sourceLang) => {
  return `${sourceLang}:${targetLang}:${text.substring(0, 100)}`;
};

/**
 * Component that automatically translates content when language changes
 * Caches translations in memory and uses server-side cache
 */
const TranslatedContent = ({ 
  content, 
  field = 'sv', 
  as: Component = 'span',
  className = '',
  html = false,
  ...props 
}) => {
  const { language } = useLanguage();
  const [translatedText, setTranslatedText] = useState(content[field] || content['sv'] || '');
  const [isTranslating, setIsTranslating] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const translateContent = async () => {
      // If content already has translation for this language, use it
      if (content[language]) {
        setTranslatedText(content[language]);
        return;
      }

      // If Swedish or same as source, no translation needed
      if (language === 'sv' || language === field) {
        setTranslatedText(content[field] || content['sv'] || '');
        return;
      }

      const sourceText = content[field] || content['sv'] || '';
      if (!sourceText || sourceText.length === 0) {
        setTranslatedText('');
        return;
      }

      // Check in-memory cache first
      const cacheKey = getCacheKey(sourceText, language, 'sv');
      if (translationCache.has(cacheKey)) {
        setTranslatedText(translationCache.get(cacheKey));
        return;
      }

      // Translate via API (which also has server-side caching)
      try {
        setIsTranslating(true);
        const result = await translateText(sourceText, language, 'sv');
        
        if (mountedRef.current) {
          const translated = result.translated || sourceText;
          setTranslatedText(translated);
          
          // Store in local cache
          translationCache.set(cacheKey, translated);
        }
      } catch (error) {
        console.error('Translation error:', error);
        if (mountedRef.current) {
          setTranslatedText(sourceText);
        }
      } finally {
        if (mountedRef.current) {
          setIsTranslating(false);
        }
      }
    };

    translateContent();
  }, [language, content, field]);

  // Render with HTML if html prop is true (sanitized to prevent XSS)
  if (html) {
    return (
      <Component 
        className={className} 
        dangerouslySetInnerHTML={createSanitizedMarkup(translatedText)}
        {...props}
      />
    );
  }

  return (
    <Component className={className} {...props}>
      {isTranslating ? (
        <span className="opacity-70">{translatedText}</span>
      ) : (
        translatedText
      )}
    </Component>
  );
};

export default TranslatedContent;
